import classNames from 'classnames';
import { useSafeAreaInsets } from 'hooks/useSafeAreaInsets';
import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import styles from './Sticky.module.scss';

export type StickyProps = {
  children: ReactNode;
  stickyChildren?: ReactNode;
  top?: number;
  zIndex?: number;
  className?: string;
  stickyClassName?: string;
  transitionDuration?: number;
};

export const Sticky: FC<StickyProps> = ({
  children,
  stickyChildren,
  top = 0,
  zIndex,
  className,
  stickyClassName,
  transitionDuration = 200,
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const { top: topSaveArea } = useSafeAreaInsets();

  const _top = top + topSaveArea;

  useEffect(() => {
    const updateDimensions = () => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.offsetHeight);
        setContentWidth(contentRef.current.offsetWidth);
      }
    };

    updateDimensions();

    // ist besser als auf window resize
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    window.addEventListener('resize', updateDimensions);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  useEffect(() => {
    const marker = ref.current;
    if (!marker) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: `-${_top}px 0px 0px 0px`,
      },
    );

    observer.observe(marker);

    return () => {
      observer.disconnect();
    };
  }, [_top]);

  const displayChildren = isSticky && stickyChildren ? stickyChildren : children;

  const classes = classNames({ [styles.isSticky]: isSticky }, className, { [stickyClassName ?? '']: isSticky });

  return (
    <>
      <div ref={ref} style={{ height: '1px', marginTop: '-1px' }} />

      {isSticky && <div style={{ height: `${contentHeight}px` }} />}

      <div
        ref={contentRef}
        className={classes}
        style={{
          top: isSticky ? `${_top}px` : undefined,
          zIndex: isSticky ? (zIndex ?? 'var(--z-index-sticky)') : undefined,
          transition: `all ${transitionDuration}ms ease-in-out`,
          width: isSticky ? `${contentWidth}px` : undefined,
          left: isSticky ? contentRef.current?.getBoundingClientRect().left : undefined,
        }}
      >
        {displayChildren}
      </div>
    </>
  );
};
